import s from './RestaurantHomeFilters.module.sass'

import { FC, memo, useContext } from 'react'
import { useRouterCompat } from '@shared/lib/hooks/useRouterCompat'

import { GlobalContext, type TypeGlobalContext } from '@app/context'
import { useAppSelector } from '@app/model/store/client'

import { usePageGlobal } from '@app/PageLayout/pageGlobalContext'
import { RestaurantQuickFilter } from '@widgets/Restaurant/RestaurantQuickFilter'
import type { TypeQuickFilter } from '@shared/api/middleware/mappingAPI/city/types'

import { RestaurantFilters } from '@features/Restaurant/RestaurantFilters'
import { TypeOutput } from '@features/Restaurant/RestaurantFilters/model/types'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { SectionTitle } from '@shared/ui/Layout/SectionTitle'

type RestaurantHomeFiltersProps = {
  requestPath?: string
  className?: string
  quickFilters?: TypeQuickFilter[] | null
  quickFiltersLoading?: boolean
  titleTag?: 'h1' | 'h2'
}

const RestaurantHomeFilters: FC<RestaurantHomeFiltersProps> = ({
  requestPath = '',
  className = '',
  quickFilters,
  quickFiltersLoading,
  titleTag = 'h1',
}) => {
  const router = useRouterCompat()
  const globalTuple = useContext(GlobalContext)
  const setGlobalContext = globalTuple?.[1]
  const citySlug = router.query?.city_slug as string | undefined
  const storeCityRead = useAppSelector((state) => state.global.cityRead)
  const currentCity = useAppSelector((state) => state.global.currentCity)
  const pageGlobal = usePageGlobal()
  const pageCity = pageGlobal?.this_city?.slug ? pageGlobal.this_city : null

  const citySource = storeCityRead ?? currentCity ?? pageCity
  const citySlugFallback = requestPath?.split('/').filter(Boolean)[0] ?? ''
  const resolvedSlug = citySource?.slug ?? citySlugFallback
  const resolvedGenitive = citySource?.genitive_case ?? ''

  const handleFilterData = (data: TypeOutput) => {
    if (citySlug ?? resolvedSlug) {
      setGlobalContext?.((c: TypeGlobalContext) => ({ ...c, isRoutePending: true }))
      router.push(`/${citySlug ?? resolvedSlug}/restaurants/restaurant_list/${data.urlQuery}`, undefined, {
        scroll: false,
      })
    }
  }

  const sectionTitleData = {
    titleText: resolvedGenitive ? `Рестораны ${resolvedGenitive}` : 'Рестораны',
    link: resolvedSlug ? `/${resolvedSlug}/restaurants/restaurant_list/` : '/restaurants/restaurant_list/',
  }

  return (
    <BlockBoundary>
      <div className={`${s.wrapper} ${className}`}>
        <SectionTitle
          tagTitle={titleTag}
          className={s.quickFilterTitle}
          title={sectionTitleData.titleText}
          link={sectionTitleData.link}
          skeleton={!citySource}
        />

        <RestaurantFilters
          preloadingResults={false}
          selected={[]}
          handleOutputData={handleFilterData}
          citySlug={resolvedSlug}
        />

        <RestaurantQuickFilter
          className={s.quickFilter}
          citySlug={resolvedSlug}
          list={quickFilters}
          loading={quickFiltersLoading}
        />
      </div>
    </BlockBoundary>
  )
}

export default memo(RestaurantHomeFilters)
